/// \file navbar.tsx
///
/// \par Copyright Notice:
/// Copyright (c) 2023 by Oshkosh Corporation.
/// All Rights Reserved.
///
/// \par
/// The information contained herein is confidential and proprietary property
/// of Oshkosh Corporation, and may be privileged.  The dissemination,
/// distribution, use, copying, transfer, or disclosure of such information is
/// strictly prohibited except by express written agreement with Oshkosh
/// Corporation, or with one of its subsidiaries.

import { Fragment } from 'react';
import { useNavigate, useSubmit } from 'react-router-dom';
import {
  AppBar,
  AppBarSection,
  AppBarSpacer,
  Menu,
  MenuItem,
  MenuSelectEvent,
} from '@progress/kendo-react-layout';
import React, {useState} from 'react';
import { PopupFormatter } from '../views/anywhere-popups/popups/popupFormatter';
import { Popup } from './popup';
import profileUseStates from '../views/anywhere-popups/useStates/profileUseStates';
import passwordUseStates from '../views/anywhere-popups/useStates/passwordUseStates';
import preferencesUseStates from '../views/anywhere-popups/useStates/preferencesUseStates';
const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const submit = useSubmit();
  const user = localStorage.getItem('user');

  const onSelect = (event: MenuSelectEvent) => {
    if(event.item?.data?.route) {
      // do check
      console.log("ROUTE ROUTE: ", event.item.data.route);
      if (event.item.data.route != "/auth/signin" && !document.cookie.split(";")[0].split("=")[1]){
        console.log("LOGGED OUT");
        navigate("/auth/signin");
      } else {
      navigate(event.item.data.route);
      }
    } else if (event.item?.data?.action) {
      submit(null, { method: 'post', action: event.item.data.action });
    }
    else if (event.item?.data?.popup) {
      if(user){
        const popupType = event.item.data.popup;
        if(popupType=="manage-profile"){
          setVisibleProfile(true);
        }
        else if(popupType=="change-preferences"){
          setVisiblePreferences(true);
        }
        else if(popupType=="change-password"){
          setVisiblePassword(true);
        }
      }
      else{
        console.log("no user for user changes")
      }
    }
    
  };

//popup
  //profile
  const [visibleProfile, setVisibleProfile] = useState(false);
  const profileId = "popup-profile";
  const {
    setCurrentProfile,
    pairsProfile,
  } = profileUseStates();
  
  const onInputChangeProfile = () => {
    const firstNameElement = document.getElementById(profileId+"-firstName-input") as HTMLInputElement;
    const lastNameElement = document.getElementById(profileId+"-lastName-input") as HTMLInputElement;
    const emailElement = document.getElementById(profileId+"-email-input") as HTMLInputElement;
    const officeElement = document.getElementById(profileId+"-office-input") as HTMLInputElement;
    const mobileElement = document.getElementById(profileId+"-mobile-input") as HTMLInputElement;
    const buttonElement = document.getElementById("new-profile-submission-button");
    if(firstNameElement && lastNameElement && emailElement && officeElement && mobileElement && buttonElement){
      const firstNameValue = firstNameElement.value;
      const lastNameValue = lastNameElement.value;
      const emailValue = emailElement.value;
      const officeValue = officeElement.value;
      const mobileValue = mobileElement.value;
      if(firstNameValue && lastNameValue && emailValue && officeValue && mobileValue){
        buttonElement.classList.add("clickable");
        buttonElement.classList.remove("unclickable");
      }
      else{
        buttonElement.classList.add("unclickable");
        buttonElement.classList.remove("clickable");
      }
    }
  };

  const setVisibleProfileFalse = () => {
    setVisibleProfile(false);
  };

  const updateProfile = () => {
    const firstNameElement = document.getElementById(profileId+"-firstName-input") as HTMLInputElement;
    const lastNameElement = document.getElementById(profileId+"-lastName-input") as HTMLInputElement;
    const emailElement = document.getElementById(profileId+"-email-input") as HTMLInputElement;
    const officeElement = document.getElementById(profileId+"-office-input") as HTMLInputElement;
    const mobileElement = document.getElementById(profileId+"-mobile-input") as HTMLInputElement;
    if(firstNameElement && lastNameElement && emailElement && officeElement && mobileElement){
      const firstNameValue = firstNameElement.value;
      const lastNameValue = lastNameElement.value;
      const emailValue = emailElement.value;
      const officeValue = officeElement.value;
      const mobileValue = mobileElement.value;
      if(firstNameValue && lastNameValue && emailValue && officeValue && mobileValue){
        const currentValues = {
          firstName:firstNameValue,
          lastName:lastNameValue,
          email:emailValue,
          office:officeValue,
          mobile:mobileValue,
        };
        setCurrentProfile(currentValues);
        setVisibleProfileFalse();
      }
    }
  };

  const profileDialog = <PopupFormatter
                      id = {profileId}
                      pairs={pairsProfile}
                      onInputChange={onInputChangeProfile}
                      />;

  const extraButtonProfile = [
    {id:"new-profile-submission-button", className:"defaultbutton unclickable", onClick:updateProfile, name:"OK"}
  ];

  const profileTitle = "Manage Profile";

  //preferences
  const [visiblePreferences, setVisiblePreferences] = useState(false);
  const prefId = "popup-preferences";  
  const {
    timeZoneValue,         
    deliveryTimeValue, 
    csvValue,
    deliveryDayValue,
    mapAppValue,
    mapTypeValue,
    recordsValue,
    decimalsValue,
    unitsValue,
    firstDayValue,
    dateNumberFormatValue,
    setCurrentPreferences,
    pairsPreferences,
  } = preferencesUseStates();

  const onInputChangePreferences = () => {
    const faultElement = document.getElementById(prefId+"-faults-input") as HTMLInputElement;
    const buttonElement = document.getElementById("new-preferences-submission-button");
    if(faultElement && buttonElement){
      const faultsValue = faultElement.value;
      if(faultsValue){
        buttonElement.classList.add("clickable");
        buttonElement.classList.remove("unclickable");
      }
      else{
        buttonElement.classList.add("unclickable");
        buttonElement.classList.remove("clickable");
      }
    }
  };

  const setVisiblePreferencesFalse = () => {
    setVisiblePreferences(false);
  };

  const updatePreferences = () => {
    const faultElement = document.getElementById(prefId+"-faults-input") as HTMLInputElement;
    if(faultElement){
      const faultsValue = faultElement.value;
      if(faultsValue){
        const currentValues = {
          faults:faultsValue,
          timeZone:timeZoneValue,
          deliveryTime:deliveryTimeValue,
          csv:csvValue,
          deliveryDay:deliveryDayValue,
          mapApp:mapAppValue,
          mapType:mapTypeValue,
          records:recordsValue,
          decimals:decimalsValue,
          units:unitsValue,
          firstDay:firstDayValue,
          dateNumberFormat:dateNumberFormatValue,
        };
        setCurrentPreferences(currentValues);
        setVisiblePreferencesFalse();
      }
    }
  };

  const prefDialog = <PopupFormatter
                      id = {prefId}
                      pairs={pairsPreferences}
                      onInputChange={onInputChangePreferences}
                      />;

  const extraButtonPreference = [
    {id:"new-preferences-submission-button", className:"defaultbutton unclickable", onClick:updatePreferences, name:"OK"}
  ];

  const prefTitle = "Update Preferences";
  
  //password
  const [visiblePassword, setVisiblePassword] = useState(false);
  const passwordId = "popup-password";
  const {
    questionValue,
    setCurrentPassword,
    pairsPassword,
  } = passwordUseStates();
  
  const onInputChangePassword = () => {
    const currentPasswordElement = document.getElementById(passwordId+"-currentPassword-input") as HTMLInputElement;
    const newPasswordElement = document.getElementById(passwordId+"-newPassword-input") as HTMLInputElement;
    const confirmNewPasswordElement = document.getElementById(passwordId+"-confirmNewPassword-input") as HTMLInputElement;
    const resetAnswerElement = document.getElementById(passwordId+"-resetAnswer-input") as HTMLInputElement;
    const buttonElement = document.getElementById("new-password-submission-button");
    if(currentPasswordElement && newPasswordElement && confirmNewPasswordElement && resetAnswerElement && buttonElement){
      const currentPasswordValue = currentPasswordElement.value;
      const newPasswordValue = newPasswordElement.value;
      const confirmNewPasswordValue = confirmNewPasswordElement.value;
      const resetAnswerValue = resetAnswerElement.value;
      if(currentPasswordValue && newPasswordValue && confirmNewPasswordValue && resetAnswerValue){
        buttonElement.classList.add("clickable");
        buttonElement.classList.remove("unclickable");
      }
      else{
        buttonElement.classList.add("unclickable");
        buttonElement.classList.remove("clickable");
      }
    }
  };

  const setVisiblePasswordFalse = () => {
    setVisiblePassword(false);
  };

  const updatePassword = () => {
    const currentPasswordElement = document.getElementById(passwordId+"-currentPassword-input") as HTMLInputElement;
    const newPasswordElement = document.getElementById(passwordId+"-newPassword-input") as HTMLInputElement;
    const confirmNewPasswordElement = document.getElementById(passwordId+"-confirmNewPassword-input") as HTMLInputElement;
    const resetAnswerElement = document.getElementById(passwordId+"-resetAnswer-input") as HTMLInputElement;
    if(currentPasswordElement && newPasswordElement && confirmNewPasswordElement && resetAnswerElement){
      const currentPasswordValue = currentPasswordElement.value;
      const newPasswordValue = newPasswordElement.value;
      const confirmNewPasswordValue = confirmNewPasswordElement.value;
      const resetAnswerValue = resetAnswerElement.value;
      if(currentPasswordValue && newPasswordValue && confirmNewPasswordValue && resetAnswerValue){
        if(newPasswordValue==confirmNewPasswordValue){
          const currentValues = {
            currentPassword:currentPasswordValue,
            newPassword:newPasswordValue,
            confirmNewPassword:confirmNewPasswordValue,
            resetAnswer:resetAnswerValue,
            question:questionValue,
          };
          setCurrentPassword(currentValues);
          setVisiblePasswordFalse();
        }
        else{
          newPasswordElement.value="";
          confirmNewPasswordElement.value="";
          console.log("new passwords did not match");
          const buttonElement = document.getElementById("new-password-submission-button");
          if(buttonElement){
            buttonElement.classList.add("unclickable");
            buttonElement.classList.remove("clickable");
          }
        }
      }
    }
  };

  const passwordDialog = <PopupFormatter
                      id = {passwordId}
                      pairs={pairsPassword}
                      onInputChange={onInputChangePassword}
                      />;

  const extraButtonPassword = [
    {id:"new-password-submission-button", className:"defaultbutton unclickable", onClick:updatePassword, name:"OK"}
  ];

  const passwordTitle = "Change Password";


  
  const visible = visibleProfile || visiblePreferences || visiblePassword;
  const title = visibleProfile ? profileTitle : visiblePreferences ? prefTitle : passwordTitle;
  const dialog = visibleProfile ? profileDialog : visiblePreferences ? prefDialog : passwordDialog;
  const toggleDialogClose = visibleProfile ? setVisibleProfileFalse : visiblePreferences ? setVisiblePreferencesFalse : setVisiblePasswordFalse;
  const closeButtonName = "CANCEL";
  const extraButtons = visibleProfile ? extraButtonProfile : visiblePreferences ? extraButtonPreference : extraButtonPassword;

  return (
    <Fragment>
      <AppBar style={{ background: 'white' }} >
        <AppBarSpacer style={{ width: 4 }} />
        <AppBarSection>
          <h1 className="title">CMS Portal</h1>
        </AppBarSection>
        <AppBarSpacer />
        <AppBarSection>
          <Menu onSelect={onSelect}>
            <MenuItem text='Home' data={{ route: '/' }} cssClass = "MenuItem" cssStyle={{ color: 'black'}} />
            <MenuItem text='Admin' cssClass = "MenuItem" cssStyle={{ color: 'black'}} >
              <MenuItem text='Asset Type' data={{ route: '/admin/AssetType' }} cssClass = "MenuItem" />
              <MenuItem text='Asset Group' data={{ route: '/admin/AssetGroup' }} cssClass = "MenuItem" />
              <MenuItem text='OEM Equipment'cssClass = "MenuItem" >
                <MenuItem text='Equipment Makes' data={{ route: '/admin/OemEquipmentMakes' }} cssClass = "MenuItem" />
                <MenuItem text='Equipment Models' data={{ route: '/admin/OemEquipmentModels' }} cssClass = "MenuItem" />
              </MenuItem>
              <MenuItem text='Notifications' data={{ route: '/admin/adminNotifications' }} cssClass = "MenuItem" />
              <MenuItem text='Notification Subscription' data={{ route: '/admin/notificationSubscription' }} cssClass = "MenuItem" />
              <MenuItem text='Maintenance Plans' data={{ route: '/admin/maintenancePlans' }} cssClass = "MenuItem" />
              <MenuItem text='AEMP' data={{ route: '/admin/aemp' }} cssClass = "MenuItem" />
              <MenuItem text='Site Management' data={{ route: '/admin/siteManagement' }} cssClass = "MenuItem" />
              <MenuItem text='Alerts Management' data={{ route: '/admin/alertsManagement' }} cssClass = "MenuItem" />
              <MenuItem text='Set System Notifications' data={{ route: '/admin/setSystemNotifications' }} cssClass = "MenuItem" />
            </MenuItem>
            <MenuItem text='Customer' cssClass = "MenuItem" cssStyle={{ color: 'black'}} >
              <MenuItem text='Customers' data={{ route: '/admin/customers' }} cssClass = "MenuItem" />
              <MenuItem text='Customer Contact Manage' data={{ route: '/admin/customers/customerContactManage' }} cssClass = "MenuItem" />
              <MenuItem text='Customer Contact Type' data={{ route: '/admin/customers/customerContactType' }} cssClass = "MenuItem" />
            </MenuItem>
            <MenuItem text='Security' cssClass = "MenuItem" cssStyle={{ color: 'black'}} >
              <MenuItem text='Security Logs' data={{ route: '/security/securityLogs' }} cssClass = "MenuItem" />
              <MenuItem text='Security Roles' data={{ route: '/security/securityRoles' }} cssClass = "MenuItem" />
              <MenuItem text='Security Users' data={{ route: '/security/securityUsers' }} cssClass = "MenuItem" />
            </MenuItem>
            <MenuItem text='Fleet' cssClass = "MenuItem" cssStyle={{ color: 'black' }}>
              <MenuItem text='Asset Management' data={{ route: '/fleet/AssetManagement' }} cssClass = "MenuItem"/>
              <MenuItem text='Fleet Faults' data={{ route: '/fleet/faults' }} cssClass = "MenuItem" />
              <MenuItem text='Fleet Dashboard' data={{ route: '/fleet' }} cssClass = "MenuItem" />
              <MenuItem text='Fleet Analytics' data={{ route: '/fleet/faultsAnalytics' }} cssClass = "MenuItem" />
              <MenuItem text='Fleet Activity' data={{ route: '/fleet/activity' }} cssClass = "MenuItem" />
            </MenuItem>
            <MenuItem text='Account' cssClass = "MenuItem" cssStyle={{ color: 'black' }}>
              <MenuItem text='Manage Profile' data={{ popup: 'manage-profile' }} cssClass = "MenuItem"/>
              <MenuItem text='Change Preferences' data={{ popup: 'change-preferences' }} cssClass = "MenuItem"/>
              <MenuItem text='Change Password' data={{ popup: 'change-password' }} cssClass = "MenuItem"/>
              <MenuItem text='Subscriptions' data={{ route: '/account/subscriptions' }} cssClass = "MenuItem"/>
              <MenuItem text='Release Notes' data={{ route: '/release-notes' }} cssClass = "MenuItem"/>
              <MenuItem text='System Notifications' data={{ route: '/system-notifications' }} cssClass = "MenuItem"/>
              <MenuItem text='Terms Of Service' data={{ route: '/account/user-agreements' }} cssClass = "MenuItem"/>
            </MenuItem>
            {user
              ?
                <MenuItem text= {user} cssClass = "MenuItem" cssStyle={{ color: 'black'}}>
                  <MenuItem text='Signout' data={{ action: '/auth/signout' }} cssClass = "MenuItem" cssStyle={{ color: 'black'}} />
                </MenuItem>
              :
                <MenuItem text='Login' data={{ route: '/auth/signin'}} cssClass = "MenuItem" cssStyle={{ color: 'black'}} />
            }
          </Menu>
        </AppBarSection>
        <AppBarSpacer style={{ width: 4 }} />
      </AppBar>
      <Popup
        visible = {visible}
        title = {title}
        body = {dialog}
        toggleDialogClose = {toggleDialogClose}
        closeButtonName = {closeButtonName}
        extraButtons = {extraButtons}
      />
      <style>{`
        body {
            background: #dfdfdf;
        }
        .title {
            font-size: 18px;
            margin: 0;
        }
        .MenuItem {
            color: black;
            background-color: white;
        }
     `}</style>
    </Fragment>
  );
};

export default Navbar;
